import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

// MARCA | 27/09/2026 | The quick-add instance that opened the modal from the landing featured collection.
/** @type {QuickAddComponent | null} */
let landingQuickAddOwner = null;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    this.#syncLandingModalState();
    await this.loadProductPage(this.productPageUrl);
    this.#openQuickAddModal();

    // MARCA | 26/05/2026 | Refresh Klarna on-site messaging after quick-add modal content is injected
    /** @type {any} */ (window).Klarna?.OnsiteMessaging?.refresh();
  };

  /**
   * MARCA | 27/09/2026 | Loads a product into the quick-add modal without leaving the page.
   * Landing colour swatches call this so a different colour stays inside the modal.
   * @param {string} productPageUrl
   */
  async loadProductPage(productPageUrl) {
    if (!productPageUrl) return;

    let productGrid = this.#cachedContent.get(productPageUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(productPageUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(productPageUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }
  }

  /**
   * MARCA | 27/09/2026 | Marks the shared quick-add dialog when it was opened from the landing featured collection.
   */
  #syncLandingModalState() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!dialogComponent) return;

    const fromLanding = this.#isLandingCollection();
    dialogComponent.toggleAttribute('data-landing-quick-add', fromLanding);
    landingQuickAddOwner = fromLanding ? this : null;
  }

  /**
   * MARCA | 27/09/2026 | True when this button belongs to the landing featured collection.
   * @returns {boolean}
   */
  #isLandingCollection() {
    return Boolean(this.closest('.landing-featured-collection'));
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    // MARCA | 27/09/2026 | Re-apply the landing flag at the moment the dialog opens.
    this.#syncLandingModalState();

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector(".product-details");
      const productFormComponent = productGrid.querySelector(
        "product-form-component",
      );
      const variantPicker = productGrid.querySelector("variant-picker");
      const productPrice = productGrid.querySelector("product-price");
      const productTitle = document.createElement("a");

      // MARCA | 27/09/2026 | Use the fetched product name so a colour switch inside the landing modal stays accurate.
      if (this.#isLandingCollection()) {
        const fetchedTitle = productGrid.querySelector('.view-product-title a');
        const fetchedVendor = productGrid.querySelector('.view-product-title__vendor');
        if (fetchedTitle instanceof HTMLElement && fetchedTitle.textContent) {
          this.dataset.productTitle = fetchedTitle.textContent.trim();
        }
        if (fetchedVendor instanceof HTMLElement && fetchedVendor.textContent) {
          this.dataset.productVendor = fetchedVendor.textContent.trim();
        }
      }

      productTitle.textContent = this.dataset.productTitle || "";

      // Make product title as a link to the product page
      // MARCA | 27/09/2026 | Landing featured collection keeps the modal title from opening the product page.
      if (!this.#isLandingCollection()) {
        productTitle.href = this.productPageUrl;
      }

      const productHeader = document.createElement("div");
      productHeader.classList.add("product-header");

      // MARCA | 26/05/2026 | Add vendor above title in mobile quick-add modal
      const vendor = this.dataset.productVendor;
      if (vendor) {
        const vendorEl = document.createElement("span");
        vendorEl.classList.add("product-header__vendor");
        vendorEl.textContent = vendor;
        productHeader.appendChild(vendorEl);
      }

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      // MARCA | 08/06/2026 | Include sibling colour swatches in mobile quick-add layout
      const colourSwatches = productGrid.querySelector(".colour-swatches");
      if (colourSwatches) {
        productGrid.appendChild(colourSwatches);
      }

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);

    // MARCA | 28/09/2026 | The fetched product form still says Add to cart. Rename it once the landing modal has that form.
    if (this.#isLandingCollection()) {
      const dialogComponent = document.getElementById('quick-add-dialog');
      if (dialogComponent instanceof QuickAddDialog) dialogComponent.applyLandingCheckoutLabel();
    }
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    // MARCA | 27/09/2026 | The landing Select Size button stays on the modal and is not replaced by a direct add.
    if (this.dataset.quickAddLocked === 'choose') return;
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#relabelLandingCheckout);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    // MARCA | 27/09/2026 | Product links inside the landing quick-add modal stay on this page. Colour swatches reload the modal.
    this.addEventListener('click', this.#keepLandingShoppersOnPage, {
      capture: true,
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    // MARCA | 27/09/2026 | Do not turn the landing modal title back into a product-page link after a variant change.
    if (this.hasAttribute('data-landing-quick-add')) return;

    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  /**
   * MARCA | 27/09/2026 | Blocks product-page navigation while the landing featured collection modal is open.
   * @param {Event} event
   */
  #keepLandingShoppersOnPage = (event) => {
    if (!this.hasAttribute('data-landing-quick-add')) return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;

    let url;
    try {
      url = new URL(link.href, window.location.origin);
    } catch {
      return;
    }

    if (!url.pathname.includes('/products/')) return;

    event.preventDefault();
    event.stopPropagation();

    if (link.classList.contains('colour-swatches__item') && link.getAttribute('aria-current') !== 'true') {
      void landingQuickAddOwner
        ?.loadProductPage(link.href)
        .then(() => {
          /** @type {any} */ (window).Klarna?.OnsiteMessaging?.refresh();
        })
        .catch(() => {});
    }
  };

  /**
   * MARCA | 28/09/2026 | Variant updates replace the button label from the product page. Put Check out back when this modal is the landing one.
   */
  #relabelLandingCheckout = () => {
    if (!this.hasAttribute('data-landing-quick-add')) return;
    queueMicrotask(() => this.applyLandingCheckoutLabel());
  };

  /**
   * MARCA | 28/09/2026 | Shows Check out on an available size. Sold out and unavailable keep their own labels.
   */
  applyLandingCheckoutLabel() {
    const label = landingQuickAddOwner
      ?.closest('.landing-featured-collection')
      ?.getAttribute('data-checkout-label');
    if (!label) return;

    const buttons = this.querySelectorAll('product-form-component button[name="add"]');
    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement) || button.disabled) continue;
      const text = button.querySelector('.add-to-cart-text__content span span');
      if (text) text.textContent = label;
    }
  }

  #handleDialogClose = () => {
    // MARCA | 27/09/2026 | Clear the landing flag so the next quick-add open on any other page behaves as before.
    this.removeAttribute('data-landing-quick-add');
    landingQuickAddOwner = null;

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
